import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { TableCell } from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { formatKnowledgeRate } from "modules/backoffice/lib/knowledge/formatKnowledgeRate"
import { useTranslation } from "react-i18next"
import { BackofficeKnowledgeEvolutionResponseDataSchema } from "schema"
import { z } from "zod"

type Props = {
  row: z.infer<typeof BackofficeKnowledgeEvolutionResponseDataSchema> & {
    questionCount: number
    answeredCount: number
    answeredRate: number | null
    knowledgePendingCount: number
    score: number | null
    questionShare?: number | null
  }
  showShare?: boolean
}

export function BackofficeKnowledgeComparisonCells({ row, showShare = false }: Props) {
  const { t, i18n } = useTranslation()
  const change = row.answeredRateChangePercentagePoints
  const displayedChange = change === null ? null : Math.round(change * 10) / 10
  const formatDate = (date: string) => new Date(date).toLocaleString(i18n.language)
  const changeText =
    displayedChange === null
      ? "—"
      : t("backoffice.knowledge.evolution.value", {
          value: new Intl.NumberFormat(i18n.language, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
            signDisplay: "always",
          }).format(displayedChange === 0 ? 0 : displayedChange),
        })

  return (
    <>
      <TableCell align="right">{row.questionCount}</TableCell>
      {showShare && <TableCell align="right">{formatKnowledgeRate(row.questionShare)}</TableCell>}
      <TableCell align="right">{formatKnowledgeRate(row.answeredRate)}</TableCell>
      <TableCell align="right">{row.score?.toFixed(1) ?? "—"}</TableCell>
      <TableCell align="right">{row.knowledgePendingCount}</TableCell>
      <TableCell align="right">
        <Tooltip>
          <TooltipTrigger asChild slick>
            <span
              tabIndex={0}
              className={cn(
                displayedChange !== null && displayedChange > 0 && "text-success",
                displayedChange !== null && displayedChange < 0 && "text-destructive",
              )}
            >
              {changeText}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <div>
              {t("backoffice.knowledge.evolution.currentPeriod", {
                from: formatDate(row.comparisonPeriod.from),
                to: formatDate(row.comparisonPeriod.to),
                rate: formatKnowledgeRate(row.answeredRate),
              })}
            </div>
            <div>
              {t("backoffice.knowledge.evolution.previousPeriod", {
                from: formatDate(row.comparisonPeriod.previousFrom),
                to: formatDate(row.comparisonPeriod.previousTo),
                rate: formatKnowledgeRate(row.previousAnsweredRate),
              })}
            </div>
            {change === null && <div>{t("backoffice.knowledge.evolution.empty")}</div>}
          </TooltipContent>
        </Tooltip>
      </TableCell>
    </>
  )
}
